// screen-size + input tiers for the phone adaptation. CSS repeats these strings byte-for-byte (see the
// "Responsive" block at the end of styles/apple.css), so a component that switches markup and the CSS
// that restyles it always agree on which tier is active.
//   compact — the condensed top bar, below the 1024px desktop floor
//   phone   — portrait phones, plus landscape phones (short + touch): cards, sheets, collapsed filters
//   narrow  — portrait-width column maths only (2-up tiles, single-column forms)
//   coarse  — touch at any width: 44px targets, 16px inputs, tap paths for every hover feature
//   hover   — a device that can hover; gate hover-only behaviour on it

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

export const MQ = {
  compact: '(max-width: 1023px)',
  phone: '(max-width: 640px), (max-height: 500px) and (pointer: coarse)',
  narrow: '(max-width: 640px)',
  coarse: '(pointer: coarse)',
  hover: '(hover: hover)',
} as const;

const canMatch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/** live result of a media query; false where matchMedia is unavailable (tests, SSR). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!canMatch()) return () => {};
      const m = window.matchMedia(query);
      m.addEventListener('change', onChange);
      return () => m.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => canMatch() && window.matchMedia(query).matches,
    () => false,
  );
}

export const useIsPhone = () => useMediaQuery(MQ.phone);
export const useIsCompact = () => useMediaQuery(MQ.compact);
export const useIsCoarse = () => useMediaQuery(MQ.coarse);

/** writes --kb-inset on <html>: how much of the layout viewport the on-screen keyboard covers, so bottom
 *  sheets can ride above it (iOS doesn't resize the layout viewport; Android does, via
 *  interactive-widget=resizes-content, and then this stays ~0). Mount once (AppShell); touch only. */
export function useKeyboardInset(): void {
  const coarse = useIsCoarse();
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!coarse || !vv) return;
    const root = document.documentElement;
    let raf = 0;
    const update = () => {
      raf = 0;
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--kb-inset', `${Math.round(inset)}px`);
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    update();
    return () => {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      if (raf) cancelAnimationFrame(raf);
      root.style.removeProperty('--kb-inset');
    };
  }, [coarse]);
}

// ── back gesture closes the top overlay (touch only) ────────────────────────────────────────────
// Each open overlay pushes one history entry (same URL, router state kept), so the phone's back
// gesture closes the overlay instead of leaving the page. One window listener serves a stack, so
// only the topmost overlay reacts; a programmatic close pops our own entry and that pop is ignored.
//   - the push waits a microtask, so StrictMode's dev mount → unmount → mount pushes exactly once;
//   - while one of our own pops is in flight, a new overlay's push waits for it (an action in one sheet
//     that opens another dialog), so the two never race and history stays [page, new overlay];
//   - overlays that close together (a dialog and the confirm on top of it) unwind every entry they own.
interface OverlayEntry { id: number; close: () => void; pushed: boolean; popped: boolean; cancelled: boolean }
const overlayStack: OverlayEntry[] = [];
const waitingPush: OverlayEntry[] = [];
const closedIds = new Set<number>(); // pushed, then closed by the app while not popped
let overlaySeq = 0;
let ignorePops = 0;
let listening = false;
let unwindQueued = false;

const topOverlayId = () => (window.history.state as { __overlay?: number } | null)?.__overlay;

function pushOverlay(entry: OverlayEntry) {
  if (entry.cancelled) return;
  overlayStack.push(entry);
  const state = (window.history.state ?? {}) as Record<string, unknown>;
  window.history.pushState({ ...state, __overlay: entry.id }, '');
  entry.pushed = true;
}

/** step back over our closed entries while one is on top; returns whether a pop was started. */
function unwindClosed(): boolean {
  const id = topOverlayId();
  if (id == null || !closedIds.has(id)) return false;
  closedIds.delete(id);
  ignorePops += 1;
  window.history.back();
  return true;
}

function onOverlayPop() {
  if (ignorePops > 0) {
    ignorePops -= 1;
    if (ignorePops === 0 && !unwindClosed()) waitingPush.splice(0).forEach(pushOverlay);
    return;
  }
  const top = overlayStack.pop();
  if (top) { top.popped = true; top.close(); }
  unwindClosed(); // back landed on a stale entry of a closed overlay: keep going to the page itself
}

/** while `open` (and on a touch device), the back gesture calls `close` instead of navigating away. */
export function useBackToClose(open: boolean, close: () => void): void {
  const coarse = useIsCoarse();
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open || !coarse || typeof window === 'undefined') return;
    if (!listening) { window.addEventListener('popstate', onOverlayPop); listening = true; }
    const entry: OverlayEntry = { id: ++overlaySeq, close: () => closeRef.current(), pushed: false, popped: false, cancelled: false };
    queueMicrotask(() => {
      if (entry.cancelled) return;
      if (ignorePops > 0) waitingPush.push(entry);
      else pushOverlay(entry);
    });
    return () => {
      entry.cancelled = true;
      const w = waitingPush.indexOf(entry);
      if (w >= 0) waitingPush.splice(w, 1);
      const i = overlayStack.indexOf(entry);
      if (i >= 0) overlayStack.splice(i, 1);
      // closed by a button/scrim: drop our entry quietly once it is on top. If a link inside the overlay
      // navigated away, the top entry is the new page's and history is left alone.
      if (!entry.pushed || entry.popped) return;
      closedIds.add(entry.id);
      if (!unwindQueued) {
        unwindQueued = true;
        queueMicrotask(() => { unwindQueued = false; if (ignorePops === 0) unwindClosed(); });
      }
    };
  }, [open, coarse]);
}
